import styled, { css } from '@emotion/native';
import {
  H3,
  AsterisksText,
  BodyText,
  H2,
  BaseText,
} from '@/shared/components/StyledText';
import { Input } from '@/shared/styles/styledComponents/styledComponents';
import { white, green, mint, lightGreen, sand } from '@/shared/styles/colors';
import { IsDesktopProps } from '@/shared/styles/types';

export const Container = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  flex: 1,
  alignSelf: 'stretch',
  height: '100%',
  backgroundColor: isDesktop ? white : sand,
}));

export const FORM_MOBILE_PADDING = 34;

export const FormContainer = styled.View<
  IsDesktopProps & { paddingBottom?: number }
>(({ isDesktop, paddingBottom }) => ({
  flex: 1,
  paddingHorizontal: isDesktop ? 42 : FORM_MOBILE_PADDING,
  paddingTop: isDesktop ? 40 : 50,
  paddingBottom: paddingBottom ?? 46,
  backgroundColor: mint,
  borderRadius: 8,
  marginBottom: isDesktop ? 28 : 0,
}));

export const FormSection = styled.View<
  IsDesktopProps & { marginBottom?: number }
>(({ isDesktop, marginBottom }) => ({
  marginBottom: marginBottom ?? (isDesktop ? 36 : 24),
}));

export const SectionHeading = styled(H3)<IsDesktopProps>(({ isDesktop }) => ({
  marginBottom: isDesktop ? 32 : 20,
  color: green,
  textTransform: 'uppercase',
}));

export const SubSection = styled.View<{ marginBottom?: number }>(
  ({ marginBottom = 0 }) => ({
    marginBottom,
  }),
);

export const StyledAsterisksText = styled(AsterisksText)<{
  marginBottom?: number;
}>(({ marginBottom = 10 }) => ({
  marginBottom,
  color: green,
  fontWeight: 'normal',
  fontSize: 16,
}));

export const DescriptionText = styled(BodyText)<{ marginBottom?: number }>(
  ({ marginBottom = 13 }) => ({
    marginBottom,
    fontSize: 15,
    color: lightGreen,
    lineHeight: 20.5,
  }),
);

export const OuterImageContainer = styled.View<IsDesktopProps>(
  ({ isDesktop }) => ({
    width: '100%',
    alignItems: isDesktop ? 'baseline' : 'center',
    marginBottom: isDesktop ? 52 : 24,
  }),
);

export const ImageContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  width: isDesktop ? 188 : 236,
  height: isDesktop ? 194 : 243,
  borderRadius: 6,
  backgroundColor: white,
  justifyContent: 'center',
  alignItems: 'center',
}));

export const ProfileImage = styled.Image({
  width: '100%',
  height: '100%',
  borderRadius: 6,
});

export const InputRow = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  flexDirection: isDesktop ? 'row' : 'column',
  gap: isDesktop ? 8 : 0,
}));

// Columns share an InputRow equally (like the name fields), so edges line up
export const InputColumnSection = styled.View<IsDesktopProps>(
  ({ isDesktop }) => ({
    ...(isDesktop ? { flex: 1, minWidth: 0 } : { width: 'auto' }),
    justifyContent: isDesktop ? 'space-between' : 'center',
  }),
);

export const StyledInput = styled(Input)({
  flex: 1,
  marginBottom: 0,
  // Same size and text inset as StaticInput
  height: 50,
  paddingLeft: 10,
});

export const inputBorderStyle = css({
  borderWidth: 1,
  borderColor: mint,
  borderRadius: 36,
});

export const largeInputBorderStyle = css({
  borderWidth: 1,
  borderColor: mint,
  borderRadius: 8,
});

export const EditButton = styled.TouchableOpacity<{
  top?: number;
  bottom?: number;
}>(({ top, bottom }) => ({
  position: 'absolute',
  bottom: bottom ?? 'auto',
  top: top ?? 'auto',
  right: 9,
  borderRadius: 6,
}));

export const ScrollUpContainer = styled.View({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
});

export const ScrollUpIcon = styled.Image({
  height: 45,
  width: 49,
});

export const ScrollUpText = styled(BaseText)({
  color: green,
  fontSize: 10,
  fontWeight: 'bold',
  textTransform: 'uppercase',
  letterSpacing: 0.01,
  textAlign: 'center',
  marginTop: 10,
});

export const IntroContentContainer = styled.View<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    flexDirection: 'column',
    justifyContent: isDesktop ? 'space-between' : 'center',
    alignItems: isDesktop ? 'flex-start' : 'center',
    marginBottom: isDesktop ? 28 : 32,
    width: '100%',
    flexWrap: 'wrap',
    backgroundColor: isDesktop ? white : sand,
    maxWidth: isDesktop ? 'auto' : 342,
    marginHorizontal: 'auto',
  }),
);

export const PageTitle = styled(H2)({
  marginBottom: 6,
  color: green,
  textTransform: 'uppercase',
  textAlign: 'center',
});

export const FlexGroup = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  display: 'flex',
  flexDirection: isDesktop ? 'row' : 'column',
  alignItems: isDesktop ? 'flex-end' : 'center',
  justifyContent: 'space-between',
  width: '100%',
  marginTop: isDesktop ? 4 : 0,
}));

export const ButtonWrapper = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  marginVertical: isDesktop ? 0 : 24,
  order: isDesktop ? 2 : 1,
  flexDirection: 'row',
  gap: isDesktop ? 22 : 10,
  alignItems: 'flex-end',
}));

export const RequiredText = styled(AsterisksText)<IsDesktopProps>(
  ({ isDesktop }) => ({
    color: green,
    fontSize: isDesktop ? 16 : 14,
    marginBottom: 0,
    order: isDesktop ? 1 : 2,
  }),
);

export const StyledBodyText = styled(BodyText)<IsDesktopProps>(
  ({ isDesktop }) => ({
    color: lightGreen,
    textAlign: isDesktop ? 'left' : 'center',
  }),
);
